import React, { useEffect, useState } from 'react';
import '../CSS/ReportsGrid.css';

const ReportsGrid = ({
  searchTerm,
  selectedSector,
  groupResponsible,
  targetedEntity,
  selectedDropdownFilter,
  startDate,
  endDate,
  selectedIndustryFilter,
  selectedRegion, // Added selectedRegion
  stateSearch,    // Added stateSearch prop
}) => {
  const [reports, setReports] = useState([]);
  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    // Fetch reports data from your server
    fetch('/api/hello')
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        // ✅ Use everything in the reports list, no filtering
        setReports(Array.isArray(data.reports) ? data.reports : []);
        setOrgs(data.orgs ?? []);
      })
      .catch((error) => {
        console.error('Error fetching reports data:', error);
        setReports([]);
        setOrgs([]);
      });
  }, []);
  

  

  useEffect(() => {
     // This will now log correctly
  }, [orgs]);

  // Updated filterReports function to include stateSearch
  const filterReports = (
    reports,
    searchTerm,
    selectedSector,
    groupResponsible,
    targetedEntity,
    selectedDropdownFilter,
    startDate,
    endDate,
    selectedIndustryFilter,
    selectedRegion,
    orgs,
    stateSearch
  ) => {
    // Normalize the search term
    const groupResponsibleLower = groupResponsible?.toLowerCase();

    // Set to store all related Group Responsible names for broader search
    let relatedGroupNames = new Set();

    if (groupResponsibleLower && orgs.length > 0) {
      // Collect all possible matching names
      orgs.forEach(org => {
        const title = org.fields?.Title?.toLowerCase();
        const aka = org.fields?.AKA?.toLowerCase();
        const oldName = org.fields?.OldName?.toLowerCase();

        // Check if input is part of any of the fields (partial match)
        if (
          (title && title.includes(groupResponsibleLower)) || 
          (aka && aka.includes(groupResponsibleLower)) || 
          (oldName && oldName.includes(groupResponsibleLower))
        ) {
          // Add all relevant names for broader filtering
          if (title) relatedGroupNames.add(title);
          if (aka) relatedGroupNames.add(aka);
          if (oldName) relatedGroupNames.add(oldName);
        }
      });

      // If no exact match found, use the input itself to allow partial search
      if (relatedGroupNames.size === 0) {
        relatedGroupNames.add(groupResponsibleLower);
      }
    }

    

    return (reports || []).filter((report) => {
      if (!report.fields) return false; // Ensure report.fields exists

      const sector = report.fields.field_6 || [];
      const searchTermLower = searchTerm?.toLowerCase();

      const titleMatches = 
        !searchTerm || 
        report.fields.Title?.toLowerCase().includes(searchTermLower);

      const groupResponsibleMatches = 
        !groupResponsible || 
        [...relatedGroupNames].some(name =>
          report.fields.field_10?.toLowerCase().includes(name) || // Partial match in field_10
          (report.fields.GroupResponsibleLU?.some(item =>
            item.LookupValue?.toLowerCase().includes(name)
          ))
        );

      const targetedEntityMatches =
        !targetedEntity ||
        (report.fields.field_11 &&
          report.fields.field_11.toLowerCase().includes(targetedEntity.toLowerCase()));

      const dropdownFilterMatches =
        !selectedDropdownFilter ||
        (report.fields.field_9 &&
          report.fields.field_9.toLowerCase().includes(selectedDropdownFilter.toLowerCase()));

      const regionMatches =
        !selectedRegion ||
        (report.fields.field_4 && report.fields.field_4 === selectedRegion);

      // Date filtering logic
      const reportDate = new Date(report.fields.field_1_formatted);
      const startDateMatches = !startDate || reportDate >= new Date(startDate);
      const endDateMatches = !endDate || reportDate <= new Date(endDate);

      const industryMatches =
        !selectedIndustryFilter ||
        (report.fields.ARIndustry &&
          report.fields.ARIndustry.toLowerCase().includes(selectedIndustryFilter.toLowerCase())) ||
        (report.fields.ESIndustry &&
          report.fields.ESIndustry.toLowerCase().includes(selectedIndustryFilter.toLowerCase())) ||
        (report.fields.PharmaIndustry &&
          report.fields.PharmaIndustry.toLowerCase().includes(selectedIndustryFilter.toLowerCase()));

      // New state search filtering logic; adjust 'State' to your actual field name if needed.
      const stateMatches =
        !stateSearch ||
        (report.fields.State &&
          report.fields.State.toLowerCase().includes(stateSearch.toLowerCase()));

      if (
        !selectedSector &&
        !searchTerm &&
        !groupResponsible &&
        !targetedEntity &&
        !selectedDropdownFilter &&
        !selectedRegion &&
        !stateSearch
      ) {
        return startDateMatches && endDateMatches && industryMatches && stateMatches;
      }

      if (selectedSector) {
        const sectorMatches = sector.some((sec) => sec.toLowerCase().includes(selectedSector.toLowerCase()));
        return (
          titleMatches &&
          sectorMatches &&
          groupResponsibleMatches &&
          targetedEntityMatches &&
          dropdownFilterMatches &&
          regionMatches &&
          startDateMatches &&
          endDateMatches &&
          industryMatches &&
          stateMatches
        );
      }

      return (
        titleMatches &&
        groupResponsibleMatches &&
        targetedEntityMatches &&
        dropdownFilterMatches &&
        regionMatches &&
        startDateMatches &&
        endDateMatches &&
        industryMatches &&
        stateMatches
      );
    });
  };

  // Apply filters (now including stateSearch)
  const filteredReports = filterReports(
    reports,
    searchTerm,
    selectedSector,
    groupResponsible,
    targetedEntity,
    selectedDropdownFilter,
    startDate,
    endDate,
    selectedIndustryFilter,
    selectedRegion,
    orgs,       // Pass the orgs array for lookup
    stateSearch // Pass the state search term
  );

  const openReport = (linkUrl, reportType = 'Daily') => {
    const userEmail = localStorage.getItem('user-email'); // ✅ Unified email
    if (!userEmail) {
      alert('You must be logged in to view this report.');
      return;
    }
  
    const parts = linkUrl.split('/');
    const filename = parts[parts.length - 1];
  
    // Encode pieces
    const encodedFilename = encodeURIComponent(filename);
    const encodedEmail = encodeURIComponent(userEmail);
  
    // ✅ Add the daily tag to the querystring
    const reportUrl = `/api/reports/${encodedFilename}?email=${encodedEmail}&tag=daily`;
  
    /*Fire-and-forget log (optional, you can keep this if you want double logging)
    fetch('/api/log-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        reportName: filename,
        reportType,      // defaults to "Daily"
        userEmail,
        tag: 'daily'     // ✅ mark the tag in your logging
      })
    }).catch(() => {}); */
  
    window.open(reportUrl, '_blank');
  };
  

  return (
    <div className="reports-grid">
      <div className="reports-header">
        <div className="header-item">Title</div>
        <div className="header-item">Report Date</div>
        <div className="header-item">Incident Date</div>
        <div className="header-item">State</div>
        <div className="header-item">Targeted Industry</div>
      </div>
      {filteredReports.map((report) => (
        <div className="report-row" key={report.fields.id}>
          {report.fields.Links1 ? (
           <a
           href="#"
           onClick={(e) => {
             e.preventDefault(); // Prevent default navigation
             openReport(report.fields.Links1); // ✅ Use unified email
           }}
           className="report-item report-title" // Keep original styling
           data-label="Title"
         >
           {report.fields.Title}
         </a>
         
          ) : (
            <div className="report-item" >{report.fields.Title}</div>
          )}
          <div className="report-item" data-label="Report Date">{report.fields.field_1_formatted}</div>
          <div className="report-item" data-label="Incident Date">{report.fields.field_2_formatted}</div>
          <div className="report-item" data-label="Country">{report.fields.field_4}</div>
          <div className="report-item" data-label="Sector">{report.fields.ARIndustry}</div>
        </div>
      ))}
    </div>
  );
};

export default ReportsGrid;
